import { createApp } from "vue";
import App from "./App.vue";
import router from "./router/index";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";

// Quasar
import { Quasar } from "quasar";
import "@quasar/extras/material-icons/material-icons.css";
import "@quasar/extras/fontawesome-v6/fontawesome-v6.css";
import "quasar/src/css/index.sass";

import { createPinia } from "pinia";
import { i18n } from "./i18n";
import { useDebugErrorStore } from "./store/debugErrorStore";
import {
  installGlobalErrorHandlers,
  formatUnknownError,
} from "./utils/globalErrorHandlers";
import { Capacitor } from "@capacitor/core";
import "./assets/main.css";
import "./assets/safe-area.css";

/**
 * En WebView/Capacitor el origen es https://localhost. Un Service Worker de PWA puede
 * dejar tras actualizar el APK cachés mezclados con rutas viejas → ChunkLoadError al
 * cargar imports dinámicos. En navegador el SW sí aporta; en native no debe controlar.
 */
const clearServiceWorkerAndCachesOnNative = async () => {
  if (!Capacitor.isNativePlatform()) return;
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch (e) {
    console.warn("MiCentrum: no se pudo desregistrar service worker:", e);
  }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {
    console.warn("MiCentrum: no se pudieron borrar caches:", e);
  }
};

const bootstrap = async () => {
  await clearServiceWorkerAndCachesOnNative();

  // Android nativo: MainActivity aplica padding inferior al marco del WebView; si el WebView
  // también expone env(safe-area-inset-bottom), .app-safe-area duplicaría el margen.
  if (typeof document !== "undefined" && Capacitor.isNativePlatform()) {
    document.documentElement.classList.add("native-app");
    if (Capacitor.getPlatform() === "ios") {
      document.documentElement.classList.add("ios-app");
    }
    if (Capacitor.getPlatform() === "android") {
      // Android nativo: MainActivity aplica padding inferior al marco del WebView.
      document.documentElement.style.setProperty(
        "--safe-area-inset-bottom",
        "0px"
      );
      document.documentElement.style.setProperty(
        "--safe-area-inset-top",
        "0px"
      );
    }
  }

  const app = createApp(App);
  const pinia = createPinia();
  app.config.globalProperties.$http = axios;
  app.use(pinia); // 💡 Aquí registramos Pinia antes de usarlo
  app.use(i18n);

  const { default: MiCentrumLoader } = await import(
    "@/components/MiCentrumLoader.vue"
  );
  app.component("MiCentrumLoader", MiCentrumLoader);

  if (Capacitor.isNativePlatform()) {
    try {
      const { registerCapacitorPushListeners } = await import(
        "@/services/capacitorPushBridge"
      );
      await registerCapacitorPushListeners();
    } catch (e) {
      console.warn("MiCentrum: registerCapacitorPushListeners:", e);
    }
  }

  const showDebugErrorOverlay = (text) => {
    try {
      useDebugErrorStore().showError(text);
    } catch {
      console.error(text);
    }
  };
  installGlobalErrorHandlers(showDebugErrorOverlay);
  app.config.errorHandler = (err, _instance, info) => {
    console.error(err, info);
    const allow =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("DEBUG_ERROR_OVERLAY") === "1";
    if (allow) {
      showDebugErrorOverlay(
        `[Vue errorHandler] ${String(info)}\n${formatUnknownError(err)}`
      );
    }
  };

  app.use(router);
  app.use(Quasar, {
    plugins: {}, // Importar plugins de Quasar cuando sea necesario
  });
  app.mount("#app");

  // Registro PWA con callbacks (updatefound / updated → banner)
  if (!Capacitor.isNativePlatform()) {
    import("./registerServiceWorker");
  }
};

void bootstrap();

